import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import {
  ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis,
  CartesianGrid, Tooltip,
} from 'recharts'
import { Card, CardContent } from '../../../../components/ui/card'
import { TrendingDown } from 'lucide-react'
import { useUnits } from '../../../../contexts/UnitsContext'
import { useIsMobile } from '../../../../hooks/useIsMobile'
import { useTheme } from '../../../../contexts/ThemeContext'

const MAX_TICKS = 5

export function WeightCaloriesChart({ series }) {
  const { t } = useTranslation()
  const { weightUnit, displayValueWeight } = useUnits()
  const isMobile = useIsMobile()
  const { primaryHsl } = useTheme()
  const primary500 = primaryHsl(500)

  const data = useMemo(
    () =>
      series.map((p) => ({
        date: new Date(`${p.date}T00:00:00`).toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
        }),
        weight: p.weight != null ? displayValueWeight(p.weight) : null,
        kcal: p.kcal != null ? p.kcal : null,
        kcalBurned: p.kcalBurned != null ? p.kcalBurned : null,
      })),
    [series, displayValueWeight]
  )

  // Evenly sampled x-axis ticks so the labels never crowd on mobile.
  const tickValues = useMemo(() => {
    if (data.length <= MAX_TICKS) return data.map((d) => d.date)
    const stride = (data.length - 1) / (MAX_TICKS - 1)
    const picked = []
    for (let i = 0; i < MAX_TICKS; i += 1) {
      picked.push(data[Math.round(i * stride)].date)
    }
    return picked
  }, [data])

  const hasAnyKcal = data.some((d) => d.kcal != null)
  const hasAnyBurned = data.some((d) => d.kcalBurned != null)
  const hasAnyWeight = data.some((d) => d.weight != null)

  if (!hasAnyKcal && !hasAnyBurned && !hasAnyWeight) {
    return (
      <Card>
        <CardContent className="p-4 text-center text-xs text-muted-foreground">
          {t('phoenixTracker.analysis.weightCalories.empty')}
        </CardContent>
      </Card>
    )
  }

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null
    const weight = payload.find((p) => p.dataKey === 'weight')?.value
    const kcal = payload.find((p) => p.dataKey === 'kcal')?.value
    const burned = payload.find((p) => p.dataKey === 'kcalBurned')?.value
    return (
      <div className="bg-gray-900 dark:bg-gray-800 text-white px-3 py-2 rounded-lg shadow-lg border border-app-primary-500/30 text-sm" dir="ltr">
        <p className="text-muted-foreground text-xs">{label}</p>
        {weight != null && (
          <p className="font-mono font-bold text-app-primary-400">
            {Number(weight).toFixed(2)} {weightUnit}
          </p>
        )}
        {kcal != null && (
          <p className="font-mono text-xs text-sky-300/90 mt-0.5">
            {Math.round(kcal)} kcal in
          </p>
        )}
        {burned != null && (
          <p className="font-mono text-xs text-emerald-300/90 mt-0.5">
            {Math.round(burned)} kcal out
          </p>
        )}
      </div>
    )
  }

  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center gap-2 mb-3">
          <TrendingDown className="w-4 h-4 text-app-primary-500" />
          <h3 className="text-sm font-semibold text-foreground" dir="auto">
            {t('phoenixTracker.analysis.weightCalories.title')}
          </h3>
        </div>

        <div dir="ltr" className="w-full">
          <ResponsiveContainer width="100%" height={isMobile ? 220 : 260}>
            <ComposedChart
              data={data}
              margin={
                isMobile
                  ? { top: 5, right: 0, left: -20, bottom: 0 }
                  : { top: 5, right: 10, left: -10, bottom: 0 }
              }
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.3} />
              <XAxis
                dataKey="date"
                ticks={tickValues}
                tick={{ fontSize: 11, fontFamily: '"JetBrains Mono", monospace' }}
                stroke="#6B7280"
              />
              <YAxis
                yAxisId="weight"
                orientation="left"
                domain={['dataMin - 1', 'dataMax + 1']}
                tick={{ fontSize: 10, fontFamily: '"JetBrains Mono", monospace' }}
                stroke={primary500}
              />
              <YAxis
                yAxisId="kcal"
                orientation="right"
                tick={{ fontSize: 10, fontFamily: '"JetBrains Mono", monospace' }}
                stroke="#0EA5E9"
              />
              <Tooltip content={<CustomTooltip />} />
              <Bar
                yAxisId="kcal"
                dataKey="kcal"
                fill="#0EA5E9"
                fillOpacity={0.45}
                radius={[2, 2, 0, 0]}
              />
              <Bar
                yAxisId="kcal"
                dataKey="kcalBurned"
                fill="#10B981"
                fillOpacity={0.6}
                radius={[2, 2, 0, 0]}
              />
              <Line
                yAxisId="weight"
                type="monotone"
                dataKey="weight"
                stroke={primary500}
                strokeWidth={2}
                dot={{ fill: primary500, strokeWidth: 0, r: 2 }}
                activeDot={{ r: 4 }}
                connectNulls
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>

        {/* Legend */}
        <div className="mt-3 pt-3 border-t border-border flex items-center flex-wrap gap-x-4 gap-y-1 text-[11px]">
          <span className="inline-flex items-center gap-1.5 text-muted-foreground">
            <span className="inline-block w-3 h-0.5 rounded-full bg-app-primary-500" />
            {t('phoenixTracker.analysis.weightCalories.weight')}
          </span>
          <span className="inline-flex items-center gap-1.5 text-muted-foreground">
            <span className="inline-block w-2.5 h-2.5 rounded-sm bg-sky-500/45" />
            {t('phoenixTracker.analysis.weightCalories.kcal')}
          </span>
          <span className="inline-flex items-center gap-1.5 text-muted-foreground">
            <span className="inline-block w-2.5 h-2.5 rounded-sm bg-emerald-500/60" />
            {t('phoenixTracker.analysis.weightCalories.kcalBurned')}
          </span>
        </div>
      </CardContent>
    </Card>
  )
}
